import { ClinicalSimulatorScenario } from '../types';

export const DEFAULT_CLINICAL_SIMULATORS: ClinicalSimulatorScenario[] = [
  {
    id: 'cna-sim-ambulate-gait-belt',
    skillId: '',
    certId: 'cna',
    version: 1,
    status: 'published',
    title: 'Ambulation with a Gait Belt Simulation',
    description: 'Safely assist Mrs. Eleanor Gable (78 y/o, post-stroke left-sided weakness) from a sitting position on the edge of the bed to ambulate 10 feet across the room and return safely to bed.',
    environment: 'bedside_room',
    imageUrl: '/images/environments/ambulation_gait_belt.jpg',
    enable3dPhotosphere: true,
    photosphereUrl: '/images/environments/ambulation_gait_belt_360.jpg',
    activeSafetyTags: ['bed_lock', 'hands_clean', 'call_light', 'curtain', 'shoes'],
    patient: {
      name: 'Mrs. Eleanor Gable',
      age: 78,
      roomNumber: '214-A',
      primaryDiagnosis: 'Ischemic Stroke (Left Hemiparesis)',
      mobilityStatus: 'Needs 1-person physical assist with transfer belt',
      precautions: ['High Fall Risk', 'Aspiration Precaution', 'Non-Skid Footwear Mandatory'],
      baselineVitals: {
        bp: '128/78 mmHg',
        hr: 74,
        rr: 16,
        spo2: 98,
        temp: '98.4°F'
      }
    },
    rubricMilestones: {
      infectionControl: {
        totalWeight: 20,
        requiredActions: ['wash_hands_entry', 'wash_hands_exit']
      },
      residentDignity: {
        totalWeight: 20,
        requiredActions: ['greet_resident', 'explain_procedure', 'close_curtain']
      },
      safetyMechanics: {
        totalWeight: 35,
        requiredActions: ['lock_bed_wheels', 'ensure_non_skid_shoes', 'apply_gait_belt_properly', 'two_finger_check', 'support_weak_side']
      },
      proceduralTechnique: {
        totalWeight: 25,
        requiredActions: ['dangle_feet_check_dizziness', 'assist_to_stand_count_of_3', 'ambulate_10_feet', 'remove_belt_seat_safely', 'provide_call_light']
      }
    },
    instantFailTraps: [
      {
        id: 'trap-no-shoes',
        name: 'Standing Without Non-Skid Footwear',
        triggerCondition: 'resident_stands_barefoot_or_regular_socks',
        boardRationale: 'Mandatory State Board Failure: Allowing a fall-risk resident to stand without non-skid footwear creates an immediate severe slip-and-fall hazard.'
      },
      {
        id: 'trap-unlocked-wheels',
        name: 'Transferring with Unlocked Bed Wheels',
        triggerCondition: 'assist_stand_with_unlocked_bed',
        boardRationale: 'State Board Safety Violation: Bed brakes must be locked before any resident transfer to prevent the bed from shifting during weight-bearing.'
      },
      {
        id: 'trap-left-dangling-alone',
        name: 'Abandoning Resident While Dangling',
        triggerCondition: 'leave_room_while_resident_dangling',
        boardRationale: 'Critical Safety Violation: A candidate must NEVER leave a dangling or standing resident unattended at the bedside.'
      }
    ],
    initialState: {
      curtainDrawn: false,
      bedHeight: 'low',
      bedLocked: false,
      handsClean: false,
      glovesOn: false,
      nonSkidShoesOn: false,
      callLightInReach: false,
      gaitBeltApplied: false,
      residentComfort: 85
    },
    hotspots: [
      {
        id: 'hotspot-sink',
        name: 'Hand Hygiene Sink Station',
        zone: 'sink',
        requiredPhase: 'any',
        availableActions: [
          {
            actionId: 'wash_hands_entry',
            label: 'Perform Hand Hygiene upon Room Entry (20s soap friction)',
            feedbackDialogue: 'Examiner notes: Candidate washes hands thoroughly with warm water, soap, lathering for 20 seconds, and dries thoroughly.',
            isCritical: true,
            pointsAwarded: 10,
            category: 'hygiene',
            stateTransitions: { handsClean: true }
          },
          {
            actionId: 'wash_hands_exit',
            label: 'Wash Hands after Removing Supplies and Completing Care',
            feedbackDialogue: 'Examiner confirms: Final hand hygiene performed properly using dry paper towel barrier for faucet.',
            isCritical: true,
            pointsAwarded: 10,
            category: 'hygiene',
            stateTransitions: { handsClean: true }
          }
        ]
      },
      {
        id: 'hotspot-curtain',
        name: 'Privacy Curtain',
        zone: 'privacy_curtain',
        requiredPhase: 'preparation',
        availableActions: [
          {
            actionId: 'close_curtain',
            label: 'Draw Privacy Curtain Around Bedside',
            feedbackDialogue: 'Mrs. Gable: "Thank you for closing that, dear. The hallway can be so busy."',
            isCritical: true,
            pointsAwarded: 10,
            category: 'dignity',
            stateTransitions: { curtainDrawn: true }
          }
        ]
      },
      {
        id: 'hotspot-bed',
        name: 'Hospital Bed & Controls',
        zone: 'bed',
        requiredPhase: 'any',
        availableActions: [
          {
            actionId: 'lock_bed_wheels',
            label: 'Inspect & Lock Bed Wheel Brakes',
            feedbackDialogue: 'Audible click: Bed caster brakes locked firmly on both sides.',
            isCritical: true,
            pointsAwarded: 15,
            category: 'safety',
            stateTransitions: { bedLocked: true }
          },
          {
            actionId: 'check_bed_height',
            label: 'Verify Bed is at Lowest Position (Resident Feet Flat on Floor)',
            feedbackDialogue: 'Examiner confirms bed is in lowest position; Mrs. Gable’s feet rest flat on the floor.',
            isCritical: false,
            pointsAwarded: 10,
            category: 'safety',
            stateTransitions: { bedHeight: 'low' }
          }
        ]
      },
      {
        id: 'hotspot-supplies',
        name: 'Overbed Supply Cart & Linen Table',
        zone: 'supplies',
        requiredPhase: 'preparation',
        availableActions: [
          {
            actionId: 'gather_gait_belt',
            label: 'Retrieve Clean Transfer/Gait Belt from Supply Cabinet',
            feedbackDialogue: 'Transfer belt inspected for intact buckle and clean webbing.',
            isCritical: false,
            pointsAwarded: 5,
            category: 'technique',
            stateTransitions: { hasGaitBelt: true }
          },
          {
            actionId: 'apply_shoes_first',
            label: 'Place Non-Skid Shoes/Socks on Resident’s Feet Prior to Standing',
            feedbackDialogue: 'Mrs. Gable smiles: "These supportive slippers help me feel steady."',
            isCritical: true,
            pointsAwarded: 15,
            category: 'safety',
            stateTransitions: { nonSkidShoesOn: true }
          }
        ]
      },
      {
        id: 'hotspot-resident',
        name: 'Resident: Mrs. Eleanor Gable',
        zone: 'resident',
        requiredPhase: 'any',
        availableActions: [
          {
            actionId: 'greet_resident',
            label: 'Knock, Greet Resident by Name & Verify ID Wristband',
            feedbackDialogue: 'Mrs. Gable: "Good morning! Yes, I am Eleanor Gable. Nice to meet you."',
            isCritical: true,
            pointsAwarded: 10,
            category: 'dignity',
            stateTransitions: { residentGreeted: true }
          },
          {
            actionId: 'explain_procedure',
            label: 'Explain Ambulation Procedure and Obtain Consent',
            feedbackDialogue: 'Mrs. Gable: "Sounds wonderful. My physical therapist said I need to walk a little today."',
            isCritical: false,
            pointsAwarded: 5,
            category: 'dignity'
          },
          {
            actionId: 'dangle_feet_check_dizziness',
            label: 'Assist to Dangle Legs over Edge of Bed & Inquire about Dizziness',
            feedbackDialogue: 'Mrs. Gable sits up, pauses: "I feel steady, no dizziness or lightheadedness at all."',
            isCritical: true,
            pointsAwarded: 10,
            category: 'safety',
            stateTransitions: { feetDangled: true }
          },
          {
            actionId: 'apply_gait_belt_properly',
            label: 'Apply Gait Belt Snugly over Resident Clothing Around Natural Waist',
            feedbackDialogue: 'Gait belt buckle fastened securely in front, slightly off-center over gown.',
            isCritical: true,
            pointsAwarded: 15,
            category: 'safety',
            stateTransitions: { gaitBeltApplied: true }
          },
          {
            actionId: 'two_finger_check',
            label: 'Perform Two-Finger Tightness Check Under Belt',
            feedbackDialogue: 'Examiner notes: Candidate verifies two flat fingers fit comfortably under belt to prevent constriction.',
            isCritical: true,
            pointsAwarded: 10,
            category: 'safety',
            stateTransitions: { twoFingerVerified: true }
          },
          {
            actionId: 'assist_to_stand_count_of_3',
            label: 'Grasp Belt with Upward Underhand Grip & Assist to Stand on Count of 3',
            feedbackDialogue: 'Candidate: "1, 2, 3, stand." Candidate knees brace resident weak side as Mrs. Gable rises smoothly.',
            isCritical: true,
            pointsAwarded: 15,
            category: 'technique',
            stateTransitions: { residentStanding: true }
          },
          {
            actionId: 'support_weak_side',
            label: 'Position Body Slightly Behind & to Weak (Left) Side During Ambulation',
            feedbackDialogue: 'Examiner checks stance: Candidate maintains an underhand hold on the belt while walking alongside left side.',
            isCritical: true,
            pointsAwarded: 10,
            category: 'technique',
            stateTransitions: { properStance: true }
          },
          {
            actionId: 'ambulate_10_feet',
            label: 'Ambulate 10 Feet at Resident’s Pace & Inquire How She Feels',
            feedbackDialogue: 'Mrs. Gable: "I am doing well, taking it one step at a time."',
            isCritical: true,
            pointsAwarded: 10,
            category: 'technique',
            stateTransitions: { ambulationDone: true }
          },
          {
            actionId: 'remove_belt_seat_safely',
            label: 'Seat Resident Safely Back in Bed/Chair & Remove Transfer Belt',
            feedbackDialogue: 'Resident safely seated with hips all the way back against the chair; transfer belt unbuckled.',
            isCritical: true,
            pointsAwarded: 10,
            category: 'safety',
            stateTransitions: { gaitBeltApplied: false, residentSeated: true }
          }
        ]
      },
      {
        id: 'hotspot-call-light',
        name: 'Bedside Call Light & Comfort',
        zone: 'call_light',
        requiredPhase: 'exit',
        availableActions: [
          {
            actionId: 'provide_call_light',
            label: 'Place Call Light Directly Within Resident’s Functional (Right) Hand',
            feedbackDialogue: 'Mrs. Gable grasps call light: "Thank you! I have it right here in my good hand."',
            isCritical: true,
            pointsAwarded: 15,
            category: 'safety',
            stateTransitions: { callLightInReach: true }
          },
          {
            actionId: 'ask_comfort_exit',
            label: 'Ask if Resident Needs Anything Else (Water, Position) Before Exit',
            feedbackDialogue: 'Mrs. Gable: "No dear, I am very comfortable. Thank you for walking with me."',
            isCritical: false,
            pointsAwarded: 5,
            category: 'dignity'
          }
        ]
      }
    ]
  },
  {
    id: 'cna-sim-hand-washing',
    skillId: '',
    certId: 'cna',
    version: 1,
    status: 'published',
    title: 'Medical Asepsis & Hand Hygiene Simulation',
    description: 'Master the 20-second clinical handwashing protocol according to CDC and State Board testing guidelines without contaminating hands or touching the sink basin.',
    environment: 'handwashing_station',
    imageUrl: '/images/environments/handwashing_station.jpg',
    enable3dPhotosphere: true,
    photosphereUrl: '/images/environments/handwashing_360.jpg',
    activeSafetyTags: ['hands_clean'],
    patient: {
      name: 'Clinical Exam Sink',
      age: 0,
      roomNumber: 'Sink Station 3',
      primaryDiagnosis: 'Standard Precautions Evaluation',
      mobilityStatus: 'N/A',
      precautions: ['Infection Control Protocol'],
      baselineVitals: {
        bp: 'N/A',
        hr: 0,
        rr: 0,
        spo2: 0,
        temp: 'N/A'
      }
    },
    rubricMilestones: {
      infectionControl: {
        totalWeight: 50,
        requiredActions: ['wet_hands_downward', 'apply_soap', 'rub_surfaces_20s', 'clean_nails_and_wrists', 'rinse_downward']
      },
      residentDignity: {
        totalWeight: 10,
        requiredActions: ['maintain_professional_demeanor']
      },
      safetyMechanics: {
        totalWeight: 20,
        requiredActions: ['avoid_sink_contact', 'check_water_temperature']
      },
      proceduralTechnique: {
        totalWeight: 20,
        requiredActions: ['dry_with_clean_towels', 'turn_off_faucet_barrier']
      }
    },
    instantFailTraps: [
      {
        id: 'trap-touch-sink',
        name: 'Hands or Uniform Touching Sink Basin',
        triggerCondition: 'touch_inside_of_sink_basin',
        boardRationale: 'Critical Infection Control Failure: Touching the contaminated sink basin or rim immediately contaminates hands and requires restarting the entire handwash.'
      },
      {
        id: 'trap-faucet-bare-hands',
        name: 'Turning Off Faucet with Bare Hands',
        triggerCondition: 'turn_off_faucet_without_barrier',
        boardRationale: 'Mandatory State Board Failure: The faucet controls are considered contaminated. Turning off the water with bare clean hands re-contaminates them instantly.'
      }
    ],
    initialState: {
      curtainDrawn: false,
      bedHeight: 'low',
      bedLocked: true,
      handsClean: false,
      glovesOn: false,
      nonSkidShoesOn: false,
      callLightInReach: false,
      residentComfort: 100
    },
    hotspots: [
      {
        id: 'hotspot-sink',
        name: 'Clinical Handwashing Sink & Faucet',
        zone: 'sink',
        coordinates: { x: 50, y: 52 },
        sphericalCoordinates: { yaw: 0, pitch: -2 },
        requiredPhase: 'any',
        availableActions: [
          {
            actionId: 'check_water_temperature',
            label: 'Turn on Water & Adjust to Warm, Comfortable Flow',
            feedbackDialogue: 'Water streams comfortably warm; water pressure adjusted to avoid splashing on clothing.',
            isCritical: false,
            pointsAwarded: 5,
            category: 'safety',
            stateTransitions: { waterFlowing: true }
          },
          {
            actionId: 'avoid_sink_contact',
            label: 'Maintain Safe Distance from Sink Rim & Uniform',
            feedbackDialogue: 'Examiner notes candidate keeps clothing 4 inches away from sink basin.',
            isCritical: true,
            pointsAwarded: 10,
            category: 'safety'
          },
          {
            actionId: 'wet_hands_downward',
            label: 'Wet Hands and Wrists Thoroughly Keeping Fingertips Pointing Downward',
            feedbackDialogue: 'Water flows down from wrists to fingertips; hands kept below elbow level at all times.',
            isCritical: true,
            pointsAwarded: 15,
            category: 'hygiene',
            stateTransitions: { handsWet: true }
          },
          {
            actionId: 'apply_soap',
            label: 'Dispense 1–2 Pumps of Liquid Soap into Palm',
            feedbackDialogue: 'Ample soap applied to palms.',
            isCritical: false,
            pointsAwarded: 5,
            category: 'hygiene',
            stateTransitions: { soapApplied: true }
          },
          {
            actionId: 'rub_surfaces_20s',
            label: 'Vigorously Rub Hands & Interlace Fingers with Friction for Full 20 Seconds',
            feedbackDialogue: 'Examiner timer starts: Candidate creates rich lather, rubbing palms, back of hands, and web spaces for 21 seconds.',
            isCritical: true,
            pointsAwarded: 25,
            category: 'hygiene',
            stateTransitions: { frictionCompleted: true }
          },
          {
            actionId: 'clean_nails_and_wrists',
            label: 'Clean Under Nails Against Opposite Palm & Wash 2 Inches Up Wrists',
            feedbackDialogue: 'Nails rotated against soapy palms; subungual areas and wrists thoroughly cleansed.',
            isCritical: true,
            pointsAwarded: 10,
            category: 'hygiene'
          },
          {
            actionId: 'rinse_downward',
            label: 'Rinse Soap from Wrists to Fingertips Pointing Downward',
            feedbackDialogue: 'All soap rinsed clean under running water without hands touching the sink or faucet.',
            isCritical: true,
            pointsAwarded: 15,
            category: 'hygiene',
            stateTransitions: { handsRinsed: true }
          }
        ]
      },
      {
        id: 'hotspot-towels',
        name: 'Paper Towel Dispenser & Waste Bin',
        zone: 'overbed_table',
        coordinates: { x: 18, y: 38 },
        sphericalCoordinates: { yaw: -58, pitch: 14 },
        requiredPhase: 'exit',
        availableActions: [
          {
            actionId: 'dry_with_clean_towels',
            label: 'Dry Hands from Fingertips to Wrists with Clean Disposable Towels',
            feedbackDialogue: 'Candidate pats hands dry starting from cleanest area (fingertips) up to wrists.',
            isCritical: true,
            pointsAwarded: 15,
            category: 'technique',
            stateTransitions: { handsClean: true }
          },
          {
            actionId: 'turn_off_faucet_barrier',
            label: 'Turn Off Water Using a Fresh, Dry Clean Paper Towel Barrier',
            feedbackDialogue: 'Examiner confirms: Faucet shut off cleanly using paper towel barrier; towel discarded immediately into waste bin without touching bin lid.',
            isCritical: true,
            pointsAwarded: 20,
            category: 'technique',
            stateTransitions: { faucetOffWithBarrier: true }
          }
        ]
      }
    ]
  },
  {
    id: 'cna-sim-radial-pulse',
    skillId: '',
    certId: 'cna',
    version: 1,
    status: 'published',
    title: 'Radial Pulse & Respiration Vital Signs Simulation',
    description: 'Perform an accurate 60-second radial pulse assessment on Mr. Arthur Bell. Requires clinical precision within ±4 BPM of the state board evaluator.',
    environment: 'bedside_room',
    imageUrl: '/images/environments/radial_pulse.jpg',
    enable3dPhotosphere: true,
    photosphereUrl: '/images/environments/radial_pulse_360.jpg',
    activeSafetyTags: ['hands_clean', 'call_light', 'curtain'],
    patient: {
      name: 'Mr. Arthur Bell',
      age: 71,
      roomNumber: '108-B',
      primaryDiagnosis: 'Congestive Heart Failure (Stable)',
      mobilityStatus: 'Independent in bed / wheelchair assist',
      precautions: ['Cardiac Diet', 'Monitor Fluid Intake'],
      baselineVitals: {
        bp: '132/82 mmHg',
        hr: 76,
        rr: 16,
        spo2: 97,
        temp: '98.6°F'
      }
    },
    rubricMilestones: {
      infectionControl: {
        totalWeight: 20,
        requiredActions: ['wash_hands_entry', 'wash_hands_exit']
      },
      residentDignity: {
        totalWeight: 20,
        requiredActions: ['greet_resident', 'explain_procedure', 'provide_privacy']
      },
      safetyMechanics: {
        totalWeight: 20,
        requiredActions: ['support_arm_comfortably', 'two_or_three_fingertips_only']
      },
      proceduralTechnique: {
        totalWeight: 40,
        requiredActions: ['locate_radial_groove', 'count_full_60_seconds', 'record_exact_bpm', 'place_call_light']
      }
    },
    instantFailTraps: [
      {
        id: 'trap-pulse-with-thumb',
        name: 'Palpating Pulse with Examiner Thumb',
        triggerCondition: 'use_thumb_for_pulse',
        boardRationale: 'Clinical Disqualification: The candidate’s thumb has its own palpable arterial pulsation and must never be used to take a resident’s pulse.'
      },
      {
        id: 'trap-short-count',
        name: 'Counting Pulse for Less than 60 Seconds',
        triggerCondition: 'count_less_than_60s',
        boardRationale: 'State Board Invalidation: Credentia and Prometric guidelines require a full 60-second continuous count for state board skill testing.'
      }
    ],
    initialState: {
      curtainDrawn: false,
      bedHeight: 'low',
      bedLocked: true,
      handsClean: false,
      glovesOn: false,
      nonSkidShoesOn: false,
      callLightInReach: false,
      residentComfort: 90
    },
    hotspots: [
      {
        id: 'hotspot-sink',
        name: 'Hygiene Station',
        zone: 'sink',
        requiredPhase: 'any',
        availableActions: [
          {
            actionId: 'wash_hands_entry',
            label: 'Wash Hands Prior to Resident Contact',
            feedbackDialogue: 'Hands sanitized properly.',
            isCritical: true,
            pointsAwarded: 10,
            category: 'hygiene',
            stateTransitions: { handsClean: true }
          },
          {
            actionId: 'wash_hands_exit',
            label: 'Wash Hands After Documenting Results',
            feedbackDialogue: 'Final hygiene verified.',
            isCritical: true,
            pointsAwarded: 10,
            category: 'hygiene'
          }
        ]
      },
      {
        id: 'hotspot-resident',
        name: 'Resident: Mr. Arthur Bell',
        zone: 'resident',
        requiredPhase: 'any',
        availableActions: [
          {
            actionId: 'greet_resident',
            label: 'Knock, Greet Mr. Bell & Check Wristband',
            feedbackDialogue: 'Mr. Bell: "Hello! Arthur Bell right here. Ready whenever you are."',
            isCritical: true,
            pointsAwarded: 10,
            category: 'dignity'
          },
          {
            actionId: 'explain_procedure',
            label: 'Explain Pulse Measurement Procedure',
            feedbackDialogue: 'Mr. Bell relaxes arm: "Sure, go ahead and check my pulse."',
            isCritical: false,
            pointsAwarded: 5,
            category: 'dignity'
          },
          {
            actionId: 'support_arm_comfortably',
            label: 'Rest Resident Arm Comfortably on Bed/Pillow (Palm Upwards)',
            feedbackDialogue: 'Arm supported in relaxed supine position with palm facing up.',
            isCritical: false,
            pointsAwarded: 5,
            category: 'safety'
          },
          {
            actionId: 'two_or_three_fingertips_only',
            label: 'Use Tips of Index and Middle Fingers (Never Thumb)',
            feedbackDialogue: 'Examiner notes proper two-finger placement over thumb-side radial groove.',
            isCritical: true,
            pointsAwarded: 15,
            category: 'technique'
          },
          {
            actionId: 'locate_radial_groove',
            label: 'Gently Compress Radial Artery until Steady Rhythm is Felt',
            feedbackDialogue: 'Steady, regular rhythmic pulse detected beneath fingertips.',
            isCritical: true,
            pointsAwarded: 10,
            category: 'technique'
          },
          {
            actionId: 'count_full_60_seconds',
            label: 'Count Pulse Beats for Full 60 Seconds by Second Hand Watch',
            feedbackDialogue: 'Clock counts: 1... 2... 3... Full 60 seconds elapsed. Candidate counts 76 beats.',
            isCritical: true,
            pointsAwarded: 25,
            category: 'technique',
            stateTransitions: { pulseMeasured: true }
          }
        ]
      },
      {
        id: 'hotspot-call-light',
        name: 'Documentation & Bedside Safety',
        zone: 'call_light',
        requiredPhase: 'exit',
        availableActions: [
          {
            actionId: 'place_call_light',
            label: 'Ensure Call Light is in Mr. Bell’s Hand',
            feedbackDialogue: 'Mr. Bell: "Got it right here. Thank you!"',
            isCritical: true,
            pointsAwarded: 15,
            category: 'safety',
            stateTransitions: { callLightInReach: true }
          },
          {
            actionId: 'record_exact_bpm',
            label: 'Record Exact Rate (76 BPM) on Candidate Documentation Sheet',
            feedbackDialogue: 'Examiner verifies documentation: Candidate records 76 BPM (Evaluator recorded 75 BPM). Within ±4 BPM tolerance — PASSED.',
            isCritical: true,
            pointsAwarded: 25,
            category: 'technique'
          }
        ]
      }
    ]
  },
  {
    id: 'phleb-sim-venipuncture',
    skillId: '',
    certId: 'phlebotomy',
    version: 1,
    status: 'published',
    title: 'Routine Venipuncture & Order of Draw Simulation',
    description: 'Perform a routine evacuated tube system (ETS) venipuncture on Mr. David Ross following CLSI H3-A6 guidelines and state phlebotomy standards.',
    environment: 'phlebotomy_chair',
    imageUrl: '/images/environments/phlebotomy_chair.jpg',
    enable3dPhotosphere: true,
    photosphereUrl: '/images/environments/phlebotomy_chair_360.jpg',
    activeSafetyTags: ['hands_clean', 'gloves'],
    patient: {
      name: 'Mr. David Ross',
      age: 52,
      roomNumber: 'Outpatient Phlebotomy Bay 2',
      primaryDiagnosis: 'Annual Health Screening (CBC & Chem-7)',
      mobilityStatus: 'Ambulatory, seated in phlebotomy arm-chair',
      precautions: ['Latex Allergy Checked (Negative)', 'Needlestick Prevention Protocol'],
      baselineVitals: {
        bp: '124/80 mmHg',
        hr: 70,
        rr: 14,
        spo2: 99,
        temp: '98.6°F'
      }
    },
    rubricMilestones: {
      infectionControl: {
        totalWeight: 30,
        requiredActions: ['sanitize_hands', 'don_gloves', 'cleanse_site_circular_friction']
      },
      residentDignity: {
        totalWeight: 15,
        requiredActions: ['verify_two_identifiers', 'explain_draw_check_fainting']
      },
      safetyMechanics: {
        totalWeight: 30,
        requiredActions: ['engage_safety_device_immediately', 'discard_needle_in_sharps']
      },
      proceduralTechnique: {
        totalWeight: 25,
        requiredActions: ['apply_tourniquet_under_1_min', 'bevel_up_15_30_degrees', 'invert_tubes_gently', 'label_at_bedside']
      }
    },
    instantFailTraps: [
      {
        id: 'trap-recapping',
        name: 'Recapping a Contaminated Phlebotomy Needle',
        triggerCondition: 'manual_recapping_of_used_needle',
        boardRationale: 'OSHA & State Board Disqualification: Contaminated needles must NEVER be manually recapped. Needle safety device must be engaged immediately and deposited directly in sharps container.'
      },
      {
        id: 'trap-tourniquet-too-long',
        name: 'Tourniquet Maintained Over 1 Minute',
        triggerCondition: 'tourniquet_exceeds_60_seconds',
        boardRationale: 'Clinical Invalidation: Leaving a tourniquet on longer than 1 minute causes hemoconcentration, invalidating laboratory test results and risking hematoma.'
      }
    ],
    initialState: {
      curtainDrawn: false,
      bedHeight: 'low',
      bedLocked: true,
      handsClean: false,
      glovesOn: false,
      nonSkidShoesOn: true,
      callLightInReach: false,
      residentComfort: 95
    },
    hotspots: [
      {
        id: 'hotspot-phleb-station',
        name: 'Phlebotomy Tray & Supplies',
        zone: 'supplies',
        requiredPhase: 'preparation',
        availableActions: [
          {
            actionId: 'sanitize_hands',
            label: 'Sanitize Hands with Alcohol Rub Prior to Tray Preparation',
            feedbackDialogue: 'Hands cleansed thoroughly.',
            isCritical: true,
            pointsAwarded: 10,
            category: 'hygiene',
            stateTransitions: { handsClean: true }
          },
          {
            actionId: 'don_gloves',
            label: 'Don Clean Nitrile Phlebotomy Gloves',
            feedbackDialogue: 'Fitted gloves donned.',
            isCritical: true,
            pointsAwarded: 10,
            category: 'hygiene',
            stateTransitions: { glovesOn: true }
          }
        ]
      },
      {
        id: 'hotspot-patient',
        name: 'Patient: Mr. David Ross',
        zone: 'resident',
        requiredPhase: 'any',
        availableActions: [
          {
            actionId: 'verify_two_identifiers',
            label: 'Ask Patient for Full Legal Name and Date of Birth Against Requisition',
            feedbackDialogue: 'Mr. Ross: "David Ross, born May 14, 1974." Matches lab requisition exactly.',
            isCritical: true,
            pointsAwarded: 15,
            category: 'dignity'
          },
          {
            actionId: 'apply_tourniquet_under_1_min',
            label: 'Apply Tourniquet 3–4 Inches Above Antecubital Fossa',
            feedbackDialogue: 'Tourniquet snug, flat, with quick-release loop pointing upwards towards shoulder.',
            isCritical: true,
            pointsAwarded: 10,
            category: 'technique'
          },
          {
            actionId: 'cleanse_site_circular_friction',
            label: 'Cleanse Median Cubital Site with 70% Isopropyl Alcohol & Allow to Air Dry 30s',
            feedbackDialogue: 'Circular concentric friction from center outwards; allowed to air-dry completely (no blowing/waving).',
            isCritical: true,
            pointsAwarded: 15,
            category: 'hygiene'
          },
          {
            actionId: 'bevel_up_15_30_degrees',
            label: 'Anchor Vein 1–2 Inches Below Site & Insert Needle Bevel Up at 15–30° Angle',
            feedbackDialogue: 'Smooth insertion, bevel facing up; flash observed, evacuated tube engages smoothly.',
            isCritical: true,
            pointsAwarded: 20,
            category: 'technique'
          },
          {
            actionId: 'engage_safety_device_immediately',
            label: 'Release Tourniquet, Withdraw Needle with Gauze, & Activate Safety Shield Instantly',
            feedbackDialogue: 'Audible snap as safety shield locks over needle tip in one single motion.',
            isCritical: true,
            pointsAwarded: 20,
            category: 'safety'
          },
          {
            actionId: 'discard_needle_in_sharps',
            label: 'Drop Shielded Needle Assembly Directly into Rigid Wall Sharps Container',
            feedbackDialogue: 'Sharps safely contained in biohazard bin.',
            isCritical: true,
            pointsAwarded: 15,
            category: 'safety'
          }
        ]
      }
    ]
  }
];

export const getSeedSimulatorForSkill = (skillId: string): ClinicalSimulatorScenario | undefined => {
  return DEFAULT_CLINICAL_SIMULATORS.find(s => s.skillId === skillId);
};
